"use client";

import type { PageOriginContext } from "@/lib/page-origin-context";
import { type ReactNode, createContext, useContext, useMemo } from "react";

export interface SearchListingContextValue {
  /** Games hub / topic context for game links from search. */
  pageOrigin: PageOriginContext | null;
  /** Game ids on the current listing page (topic, hub, daily strip) for relevance boost. */
  priorityGameIds: ReadonlySet<string>;
}

const defaultValue: SearchListingContextValue = {
  pageOrigin: null,
  priorityGameIds: new Set(),
};

const SearchListingContext = createContext<SearchListingContextValue>(defaultValue);

export function SearchListingProvider({
  pageOrigin,
  priorityGameIds,
  children,
}: {
  pageOrigin?: PageOriginContext | null;
  priorityGameIds?: Iterable<string | number>;
  children: ReactNode;
}) {
  const ids = useMemo(
    () => new Set(Array.from(priorityGameIds ?? [], (id) => String(id))),
    [priorityGameIds]
  );
  const value = useMemo(
    () => ({
      pageOrigin: pageOrigin ?? null,
      priorityGameIds: ids,
    }),
    [pageOrigin, ids]
  );
  return <SearchListingContext.Provider value={value}>{children}</SearchListingContext.Provider>;
}

export function useSearchListingContext(): SearchListingContextValue {
  return useContext(SearchListingContext);
}
